// 画面的公共部分:配色、天色、颗粒、确定性随机数。城市地图和工地剖面都用它。
// 美术方向见 11-美术.md:暖调工地剪影、限定的颜色、光从左上来、金色只给好的东西。

/** 确定性的伪随机数:同样的种子画出同样的窗灯、同样的细节。 */
export function hash(n: number): number {
  let x = (n | 0) ^ 0x9e3779b9;
  x = Math.imul(x ^ (x >>> 16), 0x85ebca6b);
  x = Math.imul(x ^ (x >>> 13), 0xc2b2ae35);
  return ((x ^ (x >>> 16)) >>> 0) / 4294967296;
}

export interface Sky {
  top: string;
  bottom: string;
  haze: string;
  night: number; // 0 白天 … 1 夜里
  name: string;
}

// 四套天色(11-美术.md):天顶、地平线、雾(r, g, b, a)、夜的程度
type Pal = { top: number[]; bottom: number[]; haze: number[]; night: number; name: string };
const DAWN: Pal = { top: [240, 168, 120], bottom: [251, 226, 194], haze: [251, 226, 194, 0.6], night: 0.15, name: '清晨' };
const DAY: Pal = { top: [121, 176, 220], bottom: [220, 237, 246], haze: [220, 237, 246, 0.55], night: 0, name: '白天' };
const DUSK: Pal = { top: [63, 74, 122], bottom: [238, 154, 107], haze: [238, 154, 107, 0.4], night: 0.45, name: '傍晚' };
const NIGHT: Pal = { top: [13, 21, 48], bottom: [39, 54, 90], haze: [39, 54, 90, 0.5], night: 1, name: '夜里' };
/** 一天里的关键钟点:两个钟点之间的颜色按比例混,天色慢慢转,不是一下子跳。 */
const KEYS: [number, Pal][] = [[0, NIGHT], [5, NIGHT], [6.5, DAWN], [8.5, DAWN], [10, DAY], [16, DAY], [17.8, DUSK], [19.2, DUSK], [21, NIGHT], [24, NIGHT]];
const forcedHour = (() => {
  const v = new URLSearchParams(location.search).get('hour');
  return v === null ? null : Number(v);
})();

const mixN = (a: number[], b: number[], t: number) => a.map((x, i) => x + (b[i] - x) * t);
const rgb = (c: number[]) => `rgb(${c.slice(0, 3).map(Math.round).join(',')})`;

/** 按游戏时钟的钟点换天色(钟点可以带小数)。网址里带 ?hour=19 可以指定钟点(检查美术用)。 */
export function skyAt(gameHour: number): Sky {
  const h = ((((forcedHour ?? gameHour) % 24) + 24) % 24);
  let k = 0;
  while (k < KEYS.length - 2 && KEYS[k + 1][0] <= h) k++;
  const [h0, a] = KEYS[k];
  const [h1, b] = KEYS[k + 1];
  const t = h1 > h0 ? (h - h0) / (h1 - h0) : 0;
  const hz = mixN(a.haze, b.haze, t);
  return {
    top: rgb(mixN(a.top, b.top, t)),
    bottom: rgb(mixN(a.bottom, b.bottom, t)),
    haze: `rgba(${hz.slice(0, 3).map(Math.round).join(',')},${hz[3].toFixed(3)})`,
    night: a.night + (b.night - a.night) * t,
    name: (t < 0.5 ? a : b).name,
  };
}

export function drawSky(g: CanvasRenderingContext2D, w: number, h: number, sky: Sky) {
  const grad = g.createLinearGradient(0, 0, 0, h);
  grad.addColorStop(0, sky.top);
  grad.addColorStop(1, sky.bottom);
  g.fillStyle = grad;
  g.fillRect(0, 0, w, h);
  if (sky.night > 0.6) {
    // 星星跟着天黑慢慢出来
    const k = Math.min(1, (sky.night - 0.6) / 0.35);
    for (let i = 0; i < 60; i++) {
      g.fillStyle = `rgba(255,255,255,${(0.3 + hash(i * 3) * 0.5) * k})`;
      const s = hash(i * 5) < 0.15 ? 1.8 : 1.1;
      g.fillRect(hash(i * 7) * w, hash(i * 13) * h * 0.5, s, s);
    }
  }
}

/** 雨:斜线。 */
export function drawRain(g: CanvasRenderingContext2D, w: number, h: number, t: number, heavy: boolean) {
  g.strokeStyle = heavy ? 'rgba(200,215,235,0.55)' : 'rgba(200,215,235,0.35)';
  g.lineWidth = 1;
  g.beginPath();
  const n = heavy ? 180 : 90;
  for (let i = 0; i < n; i++) {
    const x = (hash(i * 17) * w + t * 0.25) % w;
    const y = (hash(i * 31) * h + t * 0.9) % h;
    g.moveTo(x, y);
    g.lineTo(x - 4, y + 12);
  }
  g.stroke();
}

let grainPattern: CanvasPattern | null = null;

/** 整个画面盖一层很淡的颗粒,纯色平涂不至于显得廉价。 */
export function drawGrain(g: CanvasRenderingContext2D, w: number, h: number) {
  if (!grainPattern) {
    const c = document.createElement('canvas');
    c.width = c.height = 96;
    const x = c.getContext('2d')!;
    const img = x.createImageData(96, 96);
    for (let i = 0; i < img.data.length; i += 4) {
      const v = Math.floor(hash(i) * 255);
      img.data[i] = img.data[i + 1] = img.data[i + 2] = v;
      img.data[i + 3] = 12;
    }
    x.putImageData(img, 0, 0);
    grainPattern = g.createPattern(c, 'repeat');
  }
  if (grainPattern) {
    g.fillStyle = grainPattern;
    g.fillRect(0, 0, w, h);
  }
}

/** 楼型的外墙颜色(城市地图和剖面图一致)。 */
export const facade: Record<string, { wall: string; glass: string; lit: string; roof: string }> = {
  shophouse: { wall: '#e2c69c', glass: '#55707c', lit: '#ffd98f', roof: '#b5553d' },
  villa: { wall: '#e6d3b3', glass: '#58717f', lit: '#ffd990', roof: '#b5553d' },
  housing: { wall: '#ece6da', glass: '#4d6778', lit: '#ffd88e', roof: '#9d978f' },
  highrise: { wall: '#cfd4d8', glass: '#476173', lit: '#fbd68f', roof: '#8d8983' },
  office: { wall: '#8ea3b3', glass: '#5f7c92', lit: '#f7e2a8', roof: '#6f7d89' },
  landmark: { wall: '#34404d', glass: '#3f5569', lit: '#f5dc9c', roof: '#c9a24a' },
};

export const colors = {
  concrete: '#b8b3aa',
  concreteDark: '#8f8a82',
  rebar: '#a0522d',
  formwork: '#c8a06a',
  aluminum: '#b7bdc4',
  wet: '#6f6c67',
  net: 'rgba(76,140,86,0.45)',
  steel: '#e2a82c',
  steelDark: '#3d3218',
  gold: '#e0b24c',
  bad: '#d05a44',
  ground: '#80674a',
};

/** 按 devicePixelRatio 调整画布大小,返回 CSS 像素的宽高。 */
/** 画布按屏幕的像素密度铺满;cap 是密度的上限(游戏在走的时候压低一点,少填一半像素)。 */
export function fitCanvas(c: HTMLCanvasElement, cap = Infinity): [number, number] {
  const dpr = Math.min(window.devicePixelRatio || 1, cap);
  const w = c.clientWidth;
  const h = c.clientHeight;
  if (c.width !== Math.round(w * dpr) || c.height !== Math.round(h * dpr)) {
    c.width = Math.round(w * dpr);
    c.height = Math.round(h * dpr);
  }
  c.getContext('2d')!.setTransform(dpr, 0, 0, dpr, 0, 0);
  return [w, h];
}
